import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowUpCircle, ArrowDownCircle, ArrowLeftRight, Bot } from "lucide-react";
import { cn } from "@/lib/utils";

interface FloatingActionMenuProps {
  isOpen: boolean;
  onToggle: () => void;
  onReceita: () => void;
  onDespesa: () => void;
  onTransferencia: () => void;
  onAssistant: () => void;
}

export function FloatingActionMenu({
  isOpen,
  onToggle,
  onReceita,
  onDespesa,
  onTransferencia,
  onAssistant,
}: FloatingActionMenuProps) {
  const items = [
    {
      id: "transfer",
      label: "Transferência",
      icon: ArrowLeftRight,
      color: "bg-purple-600",
      action: onTransferencia,
    },
    {
      id: "income",
      label: "Receita",
      icon: ArrowUpCircle,
      color: "bg-green-600",
      action: onReceita,
    },
    {
      id: "expense",
      label: "Despesa",
      icon: ArrowDownCircle,
      color: "bg-red-600",
      action: onDespesa,
    },
    {
      id: "assistant",
      label: "Assistente",
      icon: Bot,
      color: "bg-primary",
      action: onAssistant,
    }
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 pointer-events-none flex justify-center items-end h-[400px]">
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Overlay Escuro */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 pointer-events-auto backdrop-blur-sm"
              onClick={onToggle}
            />

            {/* Container dos botões */}
            <div className="absolute bottom-6 left-0 right-0 flex justify-center items-end pointer-events-none mb-16">
               {/* Botões Satélites */}
               {items.map((item, index) => {
                 // Posições em arco para 4 itens
                 const positions = [
                   { x: -100, y: -20 }, // Transferência
                   { x: -35, y: -100 }, // Receita
                   { x: 35, y: -100 },  // Despesa
                   { x: 100, y: -20 },  // Assistente
                 ];

                 const pos = positions[index];

                 return (
                   <motion.div
                     key={item.id}
                     initial={{ scale: 0, opacity: 0, x: 0, y: 0 }}
                     animate={{ 
                       scale: 1, 
                       opacity: 1, 
                       x: pos.x, 
                       y: pos.y 
                     }}
                     exit={{ scale: 0, opacity: 0, x: 0, y: 0 }}
                     transition={{ 
                       type: "spring", 
                       stiffness: 300, 
                       damping: 25, 
                       delay: index * 0.05 
                     }}
                     className="absolute bottom-0 pointer-events-auto flex flex-col items-center gap-2"
                   >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        item.action();
                        onToggle();
                      }}
                      className={cn(
                        "relative w-14 h-14 rounded-full flex items-center justify-center text-white shadow-lg shadow-black/30 active:scale-95 transition-transform bg-gray-800/90 backdrop-blur-md border border-white/10"
                      )}
                    >
                       <item.icon className={cn("w-6 h-6", 
                         item.id === 'income' ? "text-green-500" :
                         item.id === 'expense' ? "text-red-500" :
                        item.id === 'assistant' ? "text-primary" :
                        "text-purple-500"
                       )} />
                     </button>
                    <span className="text-xs font-medium text-white/90 whitespace-nowrap px-2 py-1 bg-black/40 rounded-md backdrop-blur-sm text-center leading-tight">
                      {item.label}
                    </span>
                   </motion.div>
                 );
               })}
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Botão Central Principal (FAB) */}
      <div className="absolute bottom-4 pointer-events-auto">
        <button
          onClick={onToggle}
          className={cn(
            "w-12 h-12 rounded-[18px] flex items-center justify-center transition-all duration-300 shadow-xl z-50",
            isOpen 
              ? "bg-purple-600 hover:bg-purple-700 rotate-45 rounded-full" 
              : "bg-primary hover:bg-primary/90"
          )}
        >
          <Plus className={cn("text-primary-foreground transition-all", isOpen ? "w-6 h-6 text-white" : "w-5 h-5")} />
        </button>
      </div>
    </div>
  );
}
